import React from "react"

import {getEcgPxPerMm, onDevicePixelRatioChange} from "./screenScale"

/**
 * NOT a `[value, setter]` tuple, despite reading like one.
 *
 * An explicit tuple type annotation cannot be used anywhere in this project:
 * react-scripts 3.3.0 pins @typescript-eslint/parser 2.10.0, which reads
 * `TupleTypeNode.elementTypes` — a field TypeScript renamed to `elements` in
 * 4.0. Against the TypeScript 5.7 this repo compiles with, the parser crashes
 * with "Cannot read properties of undefined (reading 'map')" and the build fails
 * with no usable location. An object costs nothing and parses.
 */
export type EcgScale = {
    /** CSS px per physical millimetre, at the current zoom. */
    pxPerMm: number
    /** Re-read after <ModernScaleDialog/> stores a calibration. */
    refresh(): void
}

/**
 * The grid's CSS px per millimetre, kept correct across zoom changes.
 *
 * Zooming does not change the physical size of the screen, so the grid must draw
 * FEWER CSS pixels per millimetre as the reviewer zooms in — otherwise a major
 * square grows past 0.5 cm with every ctrl-+. `devicePixelRatio` is what moves
 * when they zoom, and ./screenScale divides the stored hardware density by it;
 * this hook is what makes React redraw when it moves.
 */
export default function useEcgPxPerMm(): EcgScale {
    const [pxPerMm, setPxPerMm] = React.useState(getEcgPxPerMm);

    const refresh = React.useCallback(() => setPxPerMm(getEcgPxPerMm()), []);

    React.useEffect(() => onDevicePixelRatioChange(refresh), [refresh]);

    return {pxPerMm, refresh}
}
